/* @moldura padrao */
/**
 * page-celular.tsx (Inbox) — a Inbox no aparelho, e não a de mesa apertada.
 *
 * ─── O QUE O PRINT DE 390px MOSTROU ─────────────────────────────────────────
 * Medido com `telas_celular.py` (nunca redimensionando janela — lei visual
 * §8.4): a tela de mesa em 390px empilha a linha em QUATRO níveis (controles /
 * nome / texto / data+ações), cada cartão passa de 100px, o assunto trunca em
 * *"Pedido de autorização esperando você: M…"* — que não diz nada — e 15 itens
 * rolam 3,2 telas.
 *
 * E o detalhe que decidiu: **nada estoura a largura**. Nenhum medidor acusa,
 * porque não é largura, é densidade. Só aparece olhando.
 *
 * ─── O QUE ELE APONTOU, E QUE MUDA O DESENHO ────────────────────────────────
 *   *"O cartão está tolerável, não está de todo ruim. O que eu estou achando
 *    ruim é a NAVEGAÇÃO — está meio bagunçada… nessa página não tem como ver o
 *    que é autorização e o que não é."*
 *
 * Ele separou certo. O problema não era o item, era **onde se está** e **como
 * se troca de lugar**. As pills que rolavam para o lado tinham dois defeitos:
 * escondem as pastas que não couberam (e o que não se vê não se procura) e não
 * dizem em qual você está sem olhar qual está pintada.
 *
 * ─── A NAVEGAÇÃO: UM BOTÃO QUE DIZ ONDE VOCÊ ESTÁ ───────────────────────────
 * No lugar das pills, um botão só, largo, com o ÍCONE + NOME da pasta atual e o
 * contador. Ele abre uma gaveta (`offcanvas` do tema, o mesmo da gaveta do
 * sino) com a lista completa, **nos mesmos grupos do desktop**.
 *
 * Três coisas melhoram de uma vez: o nome da pasta fica sempre à vista; nenhuma
 * pasta fica escondida atrás de rolagem lateral; e a estrutura é a mesma dos
 * dois aparelhos — quem aprendeu num, acha no outro.
 *
 * ─── O CARTÃO: MESMO CONTEÚDO, ARRANJO PRÓPRIO ──────────────────────────────
 * Duas linhas de texto e as ações à direita, sem coluna de seleção: no celular
 * não há ação de lote (arquivar quinze de uma vez é gesto de mesa), e um
 * checkbox por linha só gastaria a largura que o assunto precisa.
 *
 * A lógica vem toda de `shared.tsx` — o que cada pasta contém, a cor da borda,
 * o "há 5 dias", o que a estrela faz. **Aqui só há arranjo.** Se um dia as duas
 * telas discordarem sobre o que é uma pendência, é porque alguém escreveu regra
 * neste arquivo.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CustomAvatarSelo } from '@/components/CustomAvatarSelo';
import {
  badge, rotuloStatus, valorResumo, varianteStatus,
  type Autorizacao, type StatusAutorizacao,
} from '@/pages/autorizacoes/shared';
import {
  GRUPOS, ICONE_AUTORIZACAO, TIPOS, destacar, esperaDe, haQuantoTempo,
  iconeDoFiltro, quando, rotuloDoFiltro, usarInbox, type Aviso,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

export function InboxCelularPage() {
  const navigate = useNavigate();
  const ix = usarInbox();
  const [pastasAbertas, setPastasAbertas] = useState(false);
  const [buscaAberta, setBuscaAberta] = useState(false);

  function abrirAviso(a: Aviso) {
    if (!a.lida) ix.alternarLido(a);
    if (a.link) navigate(a.link);
  }

  // ── O CARTÃO DO AVISO ────────────────────────────────────────────────────
  function cartaoAviso(a: Aviso) {
    const arquivada = a.estado === 'arquivada';
    const t = quando(a.criado_em);
    const parado = haQuantoTempo(a.criado_em);
    return (
      <div key={`av-${a.id}`} className={`list-group-item ${a.lida ? '' : 'new'} ${esperaDe(a)}`}>
        <div className="row gx-3 align-items-start">
          <div className="col-auto">
            <CustomAvatarSelo foto={a.origem_foto} nome={a.origem_nome} icone={a.icone} titulo={a.titulo} />
          </div>
          <div className="col" role="button" onClick={() => abrirAviso(a)}>
            <p className={`mb-0 ${a.lida ? '' : 'fw-semibold'}`}>{a.origem_nome || a.titulo}</p>
            {/* Sem `text-truncated`: no celular a segunda linha PODE quebrar em
                duas. Truncar aqui foi o que produziu "esperando você: M…" — a
                largura é curta, e cortar o assunto tira justamente a parte que
                distingue uma linha da outra. */}
            <p className="text-secondary mb-1 small">{destacar(a.texto)}</p>
            <p className="mb-0 small text-secondary">
              {t.dia} · {t.hora}
              {parado && !arquivada && !a.lida && (
                <span className={parado.tenso ? 'text-danger ms-2' : 'ms-2'}>{parado.texto}</span>
              )}
            </p>
          </div>
          {/* As ações empilhadas à direita — alvo de dedo, e sem checkbox: no
              celular não há ação de lote. */}
          <div className="col-auto d-flex flex-column casaa-inbox-acoes">
            <button
              type="button"
              className="btn btn-sm btn-square btn-link"
              aria-label={a.favorito ? 'Tirar dos favoritos' : 'Marcar como favorito'}
              onClick={() => ix.favoritarAviso(a)}
            >
              <i className={`bi ${a.favorito ? 'bi-star-fill text-warning' : 'bi-star text-secondary'}`}></i>
            </button>
            {!arquivada && (
              <>
                {/* MARCAR COMO LIDO — faltava aqui, e ele notou: *"você não tem
                    como marcar como lida"*. No desktop esta ação mora na barra,
                    que o celular não tem; então ela desce para a linha, junto
                    das outras duas. Fechado = por ler, aberto = lido, e o
                    clique troca nos dois sentidos, igual à tela de mesa. */}
                <button
                  type="button"
                  className="btn btn-sm btn-square btn-link text-secondary"
                  aria-label={a.lida ? 'Marcar como não lido' : 'Marcar como lido'}
                  onClick={() => ix.alternarLido(a)}
                >
                  <i className={`bi ${a.lida ? 'bi-envelope-open' : 'bi-envelope-fill text-theme-1'}`}></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-square btn-link text-secondary"
                  aria-label="Arquivar"
                  onClick={() => ix.arquivar(a)}
                >
                  <i className="bi bi-archive"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── O CARTÃO DO PEDIDO ───────────────────────────────────────────────────
  function cartaoPedido(a: Autorizacao) {
    const st = a.status as StatusAutorizacao;
    const t = quando(a.criado_em);
    const parado = haQuantoTempo(a.criado_em);
    const quemPede = a.solicitante_nome || a.registrante_nome || '';
    const quanto = valorResumo(a);
    return (
      <div
        key={`au-${a.id}`}
        className={`list-group-item casaa-inbox-linha-pedido ${st === 'pendente' ? 'casaa-inbox-espera-decisao' : 'casaa-inbox-espera-fim'}`}
      >
        <div className="row gx-3 align-items-start">
          <div className="col-auto">
            <CustomAvatarSelo foto={a.solicitante_foto} nome={quemPede} icone={ICONE_AUTORIZACAO} titulo="Pedido de autorização" />
          </div>
          <div className="col" role="button" onClick={() => navigate(`/autorizacoes/${a.id}`)}>
            <div className="d-flex align-items-start justify-content-between gap-2">
              <p className="mb-0">{a.titulo}</p>
              <span className={`badge ${badge(varianteStatus(st))} flex-shrink-0`}>{rotuloStatus(st)}</span>
            </div>
            {/* O DINHEIRO EM DESTAQUE, e no celular ele ganha a linha inteira:
                é o que decide um pedido, e na tela pequena competir com o
                título fazia os dois perderem. */}
            {quanto !== '—' && <p className="mb-1 h6">{quanto}</p>}
            <p className="mb-0 small text-secondary">
              {quemPede || '—'}
              {a.entidade_label ? <> · <strong className="text-body">{a.entidade_label}</strong></> : null}
            </p>
            <p className="mb-0 small text-secondary">
              {t.dia} · {t.hora}
              {parado && st === 'pendente' && (
                <span className={parado.tenso ? 'text-danger ms-2' : 'ms-2'}>{parado.texto}</span>
              )}
            </p>
          </div>
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-sm btn-square btn-link"
              aria-label={a.favorito ? 'Tirar dos favoritos' : 'Marcar como favorito'}
              onClick={() => ix.favoritarPedido(a)}
            >
              <i className={`bi ${a.favorito ? 'bi-star-fill text-warning' : 'bi-star text-secondary'}`}></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const total = ix.contador(ix.filtro);

  return (
    <CustomMolduraPagina
      titulo="Inbox"
      acoes={
          <>
            <button
              type="button"
              className="btn btn-square btn-link"
              aria-label="Buscar"
              onClick={() => setBuscaAberta((v) => !v)}
            >
              <i className="bi bi-search"></i>
            </button>
            <button
              type="button"
              className="btn btn-square btn-link"
              aria-label="Recarregar"
              onClick={ix.carregar}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </>
      }
    >

      {/* A busca só ocupa espaço quando é pedida — numa tela de 844px de altura,
          um campo permanente custa uma linha e meia da lista. */}
      {buscaAberta && (
        <input
          type="search"
          className="form-control mb-3"
          placeholder="Buscar por assunto, pessoa ou OS…"
          autoFocus
          value={ix.busca}
          onChange={(e) => ix.setBusca(e.target.value)}
        />
      )}

      {/* ONDE VOCÊ ESTÁ — e o botão inteiro é o alvo de toque. */}
      <button
        type="button"
        className="btn btn-outline-theme w-100 d-flex align-items-center gap-2 mb-3"
        onClick={() => setPastasAbertas(true)}
      >
        <i className={`bi ${iconeDoFiltro(ix.filtro)}`}></i>
        <span className="flex-grow-1 text-start">{rotuloDoFiltro(ix.filtro)}</span>
        {total > 0 && <span className="badge text-bg-warning">{total > 99 ? '99+' : total}</span>}
        {/* ⚠️ NÃO é `chevron-down`. *"Quando você usa o drop down, dá a ideia de
            que vai cair um dropdown na tela, e não é isso."* — e ele tem razão:
            a seta para baixo é uma promessa de menu que desce. O que abre é um
            painel lateral, e `chevron-right` é o que diz isso. */}
        <i className="bi bi-chevron-right"></i>
      </button>

      {/* O SEGUNDO EIXO — que TIPO de coisa.
          *"A gente tem os avisos e a gente tem as autorizações, e eu quero que
          isso continue separado… a autorização merece destaque."* (30/08)

          ⚠️ NO CELULAR ELE É UM SEGMENTED CONTROL, e sem ícone. A primeira
          versão trouxe o mesmo desenho do desktop — ícone + texto + contador —
          e **quebrou em duas linhas**: "Autorizações" caiu sozinha embaixo, e
          ele reprovou na hora.

          A saída que ele levantou foi truncar com reticências. Não fiz, e o
          motivo é que "Autoriza…" perde justamente a palavra que precisa ser
          lida — a pasta que ele quer destacar. O que dá para tirar sem perder
          nada é o ÍCONE: aqui há três itens só, todos com o nome escrito por
          extenso ao lado, e o desenho não estava distinguindo nada que a
          palavra já não distinguisse.

          Sem ícone, os três dividem a largura em terços iguais (`flex-fill`) e
          cabem numa linha — que é a forma que os aparelhos usam para escolha
          entre poucas opções. O `text-truncate` fica como rede: se um dia um
          rótulo crescer, ele encolhe em vez de quebrar a linha. */}
      <ul className="nav nav-pills gap-1 mb-3 casaa-inbox-tipos casaa-inbox-tipos-celular">
        {TIPOS.map(({ id, rotulo }) => {
          const n = ix.contadorTipo(id);
          return (
            <li className="nav-item flex-fill" key={id}>
              <button
                type="button"
                className={`nav-link w-100 d-flex align-items-center justify-content-center gap-1 ${ix.tipo === id ? 'active' : ''}`}
                onClick={() => ix.trocarTipo(id)}
              >
                <span className="text-truncate">{rotulo}</span>
                {n > 0 && <span className="badge text-bg-warning">{n > 99 ? '99+' : n}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      {ix.recado && (
        <div className="alert alert-info alert-dismissible" role="status">
          {ix.recado}
          <button type="button" className="btn-close" aria-label="Fechar" onClick={() => ix.setRecado('')}></button>
        </div>
      )}

      <div className="list-group adminuiux-list-group list-group-flush bg-none casaa-inbox-lista">
        {ix.carregando ? (
          <div className="placeholder-glow p-3">
            <span className="placeholder col-7 mb-2"></span>
            <span className="placeholder col-4"></span>
          </div>
        ) : ix.itens.length > 0 ? (
          // No celular a lista ROLA, não pagina: o polegar já rola a página, e
          // uma seta de "próxima" no meio do caminho é um alvo a mais para
          // errar. A paginação fica na tela de mesa, onde há barra.
          ix.itens.map((it) => (it.aviso ? cartaoAviso(it.aviso) : cartaoPedido(it.pedido!)))
        ) : (
          <div className="text-center py-5">
            <span className="avatar avatar-60 rounded-circle bg-theme-1-subtle text-theme-1 d-inline-flex align-items-center justify-content-center mb-3">
              <i className="bi bi-inbox h4 mb-0"></i>
            </span>
            <p className="text-secondary mb-0">{ix.busca ? 'Nada encontrado.' : 'Nada por aqui.'}</p>
          </div>
        )}
      </div>

      {/* CARREGAR MAIS — e não setas de página.
          A paginação é do servidor (`avisos.itens_por_pagina`, na tela de
          Configurações), mas a FORMA muda com o aparelho: no dedo, uma seta de
          "próxima" é um alvo pequeno no meio do caminho, e trocar a lista
          inteira faz perder o lugar onde se estava lendo. Somar no fim continua
          de onde parou — que é o gesto que o polegar já estava fazendo.

          O número ao lado não é enfeite: sem ele, "carregar mais" não diz se
          faltam três itens ou trezentos. */}
      {ix.temMais && (
        <button
          type="button"
          className="btn btn-outline-theme w-100 mt-3"
          onClick={ix.carregarMais}
        >
          Carregar mais
          <span className="text-secondary ms-2">
            {ix.itens.length} de {ix.total}
          </span>
        </button>
      )}

      {/* A GAVETA DAS PASTAS — VEM DA DIREITA, e não de baixo.
          A primeira versão era `offcanvas-bottom` e ele fotografou o resultado:
          ela abre sobre a lista, mostra três itens e o resto exige rolar
          DENTRO de uma gaveta que já está por cima de uma lista que rola. Duas
          rolagens empilhadas, e nenhuma pista de onde termina uma.

          *"Eu faria um botão que puxa uma janela vindo da esquerda para a
          direita ou vice-versa; nessa janela você faz o select, e na hora que
          clica a gaveta recolhe."* É o que está aqui — painel lateral de altura
          cheia, onde as nove pastas cabem sem rolar.

          Mesmos grupos do desktop: a navegação muda de forma, não de conteúdo. */}
      {pastasAbertas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setPastasAbertas(false)}></div>
          <div className="offcanvas offcanvas-end show casaa-inbox-gaveta" tabIndex={-1}>
            <div className="offcanvas-header border-bottom">
              <h6 className="offcanvas-title mb-0">Ir para</h6>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setPastasAbertas(false)}></button>
            </div>
            <div className="offcanvas-body">
              {GRUPOS.map((g) => (
                <div key={g.titulo || 'topo'}>
                  {g.titulo && <p className="small text-secondary text-uppercase mt-3 mb-1">{g.titulo}</p>}
                  {g.entradas.map(({ id, rotulo, icone }) => {
                    const n = ix.contador(id);
                    const aqui = ix.filtro === id;
                    return (
                      // Marcação PRÓPRIA, e não o `nav-link` do tema: ele conta
                      // com `.row`/`.col` do grid, e dentro do `offcanvas-body`
                      // (que não é uma row) o rótulo caía embaixo do ícone e o
                      // badge embaixo do rótulo — foi o que a foto dele mostrou.
                      <button
                        key={id}
                        type="button"
                        className={`btn w-100 d-flex align-items-center gap-2 text-start mb-1 ${aqui ? 'btn-theme-1' : 'btn-link text-body'}`}
                        onClick={() => { ix.trocarFiltro(id); setPastasAbertas(false); }}
                      >
                        <i className={`bi ${icone}`}></i>
                        <span className="flex-grow-1">{rotulo}</span>
                        {n > 0 && <span className="badge text-bg-warning">{n > 99 ? '99+' : n}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </CustomMolduraPagina>
  );
}
